import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ExcelJS from 'exceljs';
import API_URL from '../../config/api';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, DollarSign, ShoppingBag, TrendingUp, Package, CreditCard, ArrowDownRight, Plus, Edit2, Trash2, Download, Filter, GripVertical, RotateCcw, Lock, Printer } from 'lucide-react';
import { toEcuadorTime, formatEcuadorTime, formatEcuadorDate, getEcuadorDateStr, getOrderCode } from '../../utils/ecuadorTime';
import CustomAlert from './shared/CustomAlert';

// Zona horaria + código de pedido por día: ver utils/ecuadorTime.js (import
// arriba). getDayCode(sale, allSales) recalculaba el número del día contando
// sobre el array `sales` cargado en el cliente — mismo bug que ya se había
// corregido en PedidosPendientes.js (Fase A #9): si `sales` no trae TODAS las
// ventas del día (filtros, orden, límite del backend), el número podía no
// coincidir con el que muestran Cocina/Mesas/Pedidos para el mismo pedido.
// Ahora se usa directamente sale.day_order_number, calculado por el backend.
const WEEKDAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];
const getDayCode = getOrderCode;

// COLORES PARA GRAFICAS
const COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  purple: '#8b5cf6',
  pink: '#ec4899',
  indigo: '#6366f1',
  teal: '#14b8a6',
  orange: '#f97316',
  cyan: '#06b6d4'
};

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#6366f1', '#14b8a6', '#f97316', '#06b6d4'];

// Orden por defecto de las graficas del dashboard (usado si el usuario no ha reordenado nada aun)
const DEFAULT_CHART_ORDER = [
  'metodos-pago', 'ventas-dia', 'top-productos-pizza', 'bottom-productos',
  'ingresos-egresos', 'ventas-tipo-pedido', 'ordenes-por-dia',
  'tendencia-ventas', 'ventas-hora', 'ventas-categoria', 'comparativa-mensual',
  'tiempo-cocina', 'tiempo-cocina-hora', 'tiempo-cocina-semana', 'tiempo-cocina-mes',
  'tiempo-cocina-detalle', 'ordenes-tiempo', 'ticket-promedio', 'distribucion-montos'
];

const ReportesUnificado = ({ token, clientData }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [customAlert, setCustomAlert] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [confirmDeletePayment, setConfirmDeletePayment] = useState(null);

  const plan = clientData?.plan || 'basico';
  const isPremiumOrHigher = plan === 'premium' || plan === 'empresarial';
  const isStandardOrHigher = plan === 'estandar' || isPremiumOrHigher;
  // ⭐ Editar un pedido ya pagado ahora es un permiso aparte (pedido de
  // Angelo, 2026-08-05): el dueño siempre puede, un empleado solo si se le
  // asignó 'editar_pedidos_pagados' explícitamente en Empleados -- no viene
  // incluido con 'caja'/'mesas'/'pedidos'. clientData.role llega como
  // 'owner' para el dueño (ver App.js) y como el rol guardado para
  // empleados.
  const canEditPaidOrders = clientData?.role === 'owner' || !!clientData?.permissions?.editar_pedidos_pagados;

  // Estados para Dashboard
  const [timeFilter, setTimeFilter] = useState('today');
  const [customDate, setCustomDate] = useState('');
  const customDateInputRef = useRef(null);

  const openCustomDatePicker = () => {
    const el = customDateInputRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch (err) {
        // Algunos navegadores lanzan error si no hay gesto directo del usuario; usamos click() como respaldo
      }
    }
    el.click();
    el.focus();
  };
  const [dashboardData, setDashboardData] = useState({
    totalSales: 0,
    totalExpenses: 0,
    totalManualIncome: 0,
    totalOrders: 0,
    balance: 0,
    bestProduct: null,
    cashVsTransfer: [],
    salesByDay: [],
    topProducts: [],
    bottomProducts: [],
    salesTrend: [],
    salesByHour: [],
    salesByCategory: [],
    monthlyComparison: [],
    incomeVsExpenses: [],
    ordersByType: [],
    orderCountByDay: [],
    avgTicketByDay: [],
    salesDistribution: [],
    kitchenAvgByDay: [],
    kitchenOnTimeRatio: [],
    kitchenHourlyToday: [],
    kitchenByWeekday: [],
    kitchenByMonthDay: [],
    kitchenTimingDetail: []
  });
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  // Orden personalizado de las graficas (arrastrar y soltar)
  const chartOrderKey = `pos_dashboard_chart_order_${clientData?.id || 'default'}`;
  const [chartOrder, setChartOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(chartOrderKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) { /* ignora errores de localStorage */ }
    return DEFAULT_CHART_ORDER;
  });
  const [draggedChartId, setDraggedChartId] = useState(null);

  const handleChartDragStart = (id) => (e) => {
    setDraggedChartId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleChartDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleChartDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!draggedChartId || draggedChartId === targetId) {
      setDraggedChartId(null);
      return;
    }
    setChartOrder(prevOrder => {
      const currentOrder = prevOrder && prevOrder.length ? [...prevOrder] : [...DEFAULT_CHART_ORDER];
      DEFAULT_CHART_ORDER.forEach(id => {
        if (!currentOrder.includes(id)) currentOrder.push(id);
      });

      const fromIndex = currentOrder.indexOf(draggedChartId);
      const toIndex = currentOrder.indexOf(targetId);
      if (fromIndex === -1 || toIndex === -1) return prevOrder;

      currentOrder.splice(fromIndex, 1);
      currentOrder.splice(toIndex, 0, draggedChartId);

      try {
        localStorage.setItem(chartOrderKey, JSON.stringify(currentOrder));
      } catch (e) { /* ignora errores de localStorage */ }

      return currentOrder;
    });
    setDraggedChartId(null);
  };

  const handleChartDragEnd = () => setDraggedChartId(null);

  const resetChartOrder = () => {
    setChartOrder(DEFAULT_CHART_ORDER);
    try {
      localStorage.removeItem(chartOrderKey);
    } catch (e) { /* ignora errores de localStorage */ }
  };

  // Estados para Ventas (Tab 2)
  const [sales, setSales] = useState([]);
  const [cashSessions, setCashSessions] = useState([]);
  const [loadingCashSessions, setLoadingCashSessions] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [stats, setStats] = useState({ total_sales: 0, total_revenue: 0 });
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);

  // ⭐ Editar pedido ya pagado desde Reportes (pedido de Angelo, 2026-08-05):
  // vista general de CUALQUIER venta pagada, sea de mesa, para llevar o de
  // Caja directa -- complementa el mismo flujo que ya existe en Mesas.js
  // para comandas de mesa. Ver PUT /api/pos/sales/:id/edit-paid.
  const [editingSaleItems, setEditingSaleItems] = useState(null); // array editable o null si no se está editando
  const [editSaleReason, setEditSaleReason] = useState('');
  const [savingSaleEdit, setSavingSaleEdit] = useState(false);

  // Estados para Pagos (integrados en Ventas)
  const [payments, setPayments] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [formData, setFormData] = useState({
    type: 'expense',
    category: 'purchase',
    description: '',
    amount: '',
    payment_method: 'cash',
    reference: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [showPaymentsSection, setShowPaymentsSection] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);

  const categories = {
    expense: [
      { value: 'purchase', label: 'Compra de Inventario' },
      { value: 'supplier', label: 'Pago a Proveedor' },
      { value: 'service', label: 'Servicios (luz, agua, etc.)' },
      { value: 'salary', label: 'Salarios' },
      { value: 'rent', label: 'Arriendo' },
      { value: 'maintenance', label: 'Mantenimiento' },
      { value: 'other', label: 'Otros Gastos' }
    ],
    income: [
      { value: 'sale', label: 'Venta Manual' },
      { value: 'refund', label: 'Devolucion/Reembolso' },
      { value: 'investment', label: 'Inversion' },
      { value: 'other', label: 'Otros Ingresos' }
    ]
  };

  const showAlert = (type, title, message) => {
    setCustomAlert({ type, title, message });
  };

  // ============================================
  // DASHBOARD: Cargar datos segun filtro de tiempo
  // ============================================
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDashboardData();
    }
  }, [activeTab, timeFilter, customDate]);

  // Cargar ventas cuando cambia fecha en Tab 2
  useEffect(() => {
    if (activeTab === 'ventas') {
      loadSales();
      loadPayments();
      loadCashSessions();
    }
  }, [activeTab, selectedDate]);

  const loadDashboardData = async () => {
    setLoadingDashboard(true);
    try {
      const [salesResponse, expensesResponse, kitchenResponse] = await Promise.all([
        axios.get(`${API_URL}/api/pos/sales`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/pos/manual-payments`, { headers: { Authorization: `Bearer ${token}` } }),
        isPremiumOrHigher
          ? axios.get(`${API_URL}/api/pos/kitchen-stats`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] })
      ]);

      const allSales = salesResponse.data;
      const allPayments = expensesResponse.data;
      const kitchenData = kitchenResponse.data;

      const nowUtc = new Date();
      const now = toEcuadorTime(nowUtc.toISOString());

      // ⭐ FIX (pedido de Angelo, 2026-08-05): un pedido 'pending' (tomado
      // pero todavía sin cobrar) no es una venta todavía -- no debe sumar en
      // ningún total/gráfica de este dashboard, solo cuando se cobra de
      // verdad (payment_status = 'paid'). Antes solo se excluían las
      // anuladas, así que "Ventas de Hoy" y compañía incluían dinero que ni
      // siquiera había entrado a caja.
      let filteredSales = allSales.filter(sale => !sale.cancelled && sale.payment_status === 'paid');

      if (timeFilter === 'today') {
        const today = getEcuadorDateStr(nowUtc.toISOString());
        filteredSales = filteredSales.filter(sale => getEcuadorDateStr(sale.created_at) === today);
      } else if (timeFilter === 'week') {
        const weekAgoUtc = new Date(nowUtc.getTime() - 7 * 24 * 60 * 60 * 1000);
        filteredSales = filteredSales.filter(sale => toEcuadorTime(sale.created_at) >= toEcuadorTime(weekAgoUtc.toISOString()));
      } else if (timeFilter === 'month') {
        const monthStartEcuador = new Date(now.getFullYear(), now.getMonth(), 1);
        filteredSales = filteredSales.filter(sale => toEcuadorTime(sale.created_at) >= monthStartEcuador);
      } else if (timeFilter === 'custom' && customDate) {
        filteredSales = filteredSales.filter(sale => getEcuadorDateStr(sale.created_at) === customDate);
      }

      // Filtrar pagos manuales segun periodo
      let filteredExpenses = allPayments.filter(p => p.type === 'expense');
      let filteredIncome = allPayments.filter(p => p.type === 'income');

      const filterByTime = (list) => {
        if (timeFilter === 'today') {
          const today = getEcuadorDateStr(nowUtc.toISOString());
          return list.filter(item => (item.date || '').split('T')[0] === today);
        } else if (timeFilter === 'week') {
          const weekAgoUtc = new Date(nowUtc.getTime() - 7 * 24 * 60 * 60 * 1000);
          return list.filter(item => new Date(item.date) >= weekAgoUtc);
        } else if (timeFilter === 'month') {
          const monthStartEcuador = new Date(now.getFullYear(), now.getMonth(), 1);
          return list.filter(item => new Date(item.date) >= monthStartEcuador);
        } else if (timeFilter === 'custom' && customDate) {
          return list.filter(item => (item.date || '').split('T')[0] === customDate);
        }
        return list;
      };

      filteredExpenses = filterByTime(filteredExpenses);
      filteredIncome = filterByTime(filteredIncome);

      // 1. Total Ventas
      const totalSales = filteredSales.reduce((sum, sale) => sum + sale.total, 0);

      // 2. Total Gastos
      const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);

      // 3. Total Ingresos Manuales
      const totalManualIncome = filteredIncome.reduce((sum, inc) => sum + inc.amount, 0);

      // 4. Total Ordenes
      const totalOrders = filteredSales.length;

      // 5. Balance
      const balance = totalSales + totalManualIncome - totalExpenses;

      // 6. Mejor Producto
      const productMap = {};
      filteredSales.forEach(sale => {
        sale.items.forEach(item => {
          if (!productMap[item.name]) {
            productMap[item.name] = { name: item.name, quantity: 0, revenue: 0 };
          }
          productMap[item.name].quantity += item.quantity;
          productMap[item.name].revenue += item.price * item.quantity;
        });
      });
      const sortedProducts = Object.values(productMap).sort((a, b) => b.quantity - a.quantity);
      const bestProduct = sortedProducts[0] || null;

      // 7. Top 5 Productos
      const topProducts = sortedProducts.slice(0, 5);

      // 8. Bottom 5 Productos (menos vendidos)
      const bottomProducts = sortedProducts.length > 1
        ? sortedProducts.slice(-5).reverse()
        : [];

      // 9. Efectivo vs Transferencia
      const cashTotal = filteredSales.filter(s => s.payment_method === 'cash').reduce((sum, s) => sum + s.total, 0);
      const transferTotal = filteredSales.filter(s => s.payment_method === 'transfer').reduce((sum, s) => sum + s.total, 0);
      const mixedTotal = filteredSales.filter(s => s.payment_method === 'mixed').reduce((sum, s) => sum + s.total, 0);

      const cashVsTransfer = [
        { name: 'Efectivo', value: cashTotal, color: COLORS.success },
        { name: 'Transferencia', value: transferTotal, color: COLORS.primary },
        { name: 'Mixto', value: mixedTotal, color: COLORS.warning }
      ].filter(item => item.value > 0);

      // 10. Ventas por Dia
      const salesByDayMap = {};
      const orderCountByDayMap = {};
      const avgTicketByDayMap = {};
      const daysToShow = timeFilter === 'month' ? 30 : 7;

      for (let i = daysToShow - 1; i >= 0; i--) {
        const date = new Date(nowUtc.getTime() - i * 24 * 60 * 60 * 1000);
        const dateStr = getEcuadorDateStr(date.toISOString());
        salesByDayMap[dateStr] = 0;
        orderCountByDayMap[dateStr] = 0;
        avgTicketByDayMap[dateStr] = { total: 0, count: 0 };
      }

      filteredSales.forEach(sale => {
        const dateStr = getEcuadorDateStr(sale.created_at);
        if (salesByDayMap.hasOwnProperty(dateStr)) {
          salesByDayMap[dateStr] += sale.total;
          orderCountByDayMap[dateStr] += 1;
          avgTicketByDayMap[dateStr].total += sale.total;
          avgTicketByDayMap[dateStr].count += 1;
        }
      });

      const salesByDay = Object.entries(salesByDayMap).map(([date, total]) => ({
        date: new Date(date + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
        total: parseFloat(total.toFixed(2))
      }));

      const orderCountByDay = Object.entries(orderCountByDayMap).map(([date, count]) => ({
        date: new Date(date + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
        ordenes: count
      }));

      const avgTicketByDay = Object.entries(avgTicketByDayMap).map(([date, data]) => ({
        date: new Date(date + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
        promedio: data.count > 0 ? parseFloat((data.total / data.count).toFixed(2)) : 0
      }));

      // 11. Tendencia de Ventas 30 dias (estandar+)
      const trendMap = {};
      for (let i = 29; i >= 0; i--) {
        const date = new Date(nowUtc.getTime() - i * 24 * 60 * 60 * 1000);
        const dateStr = getEcuadorDateStr(date.toISOString());
        trendMap[dateStr] = 0;
      }
      allSales.filter(s => !s.cancelled && s.payment_status === 'paid').forEach(sale => {
        const dateStr = getEcuadorDateStr(sale.created_at);
        if (trendMap.hasOwnProperty(dateStr)) {
          trendMap[dateStr] += sale.total;
        }
      });
      const salesTrend = Object.entries(trendMap).map(([date, total]) => ({
        date: new Date(date + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
        total: parseFloat(total.toFixed(2))
      }));

      // 12. Ventas por Hora (estandar+)
      const hourMap = {};
      for (let i = 0; i < 24; i++) hourMap[i] = 0;
      filteredSales.forEach(sale => {
        const hour = toEcuadorTime(sale.created_at).getHours();
        hourMap[hour] += sale.total;
      });
      const salesByHour = Object.entries(hourMap).map(([hour, total]) => ({
        hour: `${hour}:00`,
        total: parseFloat(total.toFixed(2))
      }));

      // 13. Ventas por Categoria (estandar+)
      const categoryMap = {};
      filteredSales.forEach(sale => {
        sale.items.forEach(item => {
          const category = item.category || 'Sin categoria';
          if (!categoryMap[category]) categoryMap[category] = 0;
          categoryMap[category] += item.price * item.quantity;
        });
      });
      const salesByCategory = Object.entries(categoryMap).map(([name, value]) => ({
        name,
        value: parseFloat(value.toFixed(2))
      }));

      // 14. Comparativa Mensual (estandar+)
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      const currentMonthSales = allSales.filter(s =>
        !s.cancelled && s.payment_status === 'paid' && new Date(s.created_at).getMonth() === currentMonth && new Date(s.created_at).getFullYear() === currentYear
      ).reduce((sum, s) => sum + s.total, 0);
      const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
      const lastMonthSales = allSales.filter(s =>
        !s.cancelled && s.payment_status === 'paid' && new Date(s.created_at).getMonth() === lastMonth && new Date(s.created_at).getFullYear() === lastMonthYear
      ).reduce((sum, s) => sum + s.total, 0);
      const monthlyComparison = [
        { name: 'Mes Anterior', ventas: parseFloat(lastMonthSales.toFixed(2)) },
        { name: 'Mes Actual', ventas: parseFloat(currentMonthSales.toFixed(2)) }
      ];

      // 15. Ingresos vs Egresos
      const incomeVsExpenses = [
        { name: 'Ventas', value: parseFloat(totalSales.toFixed(2)), color: COLORS.primary },
        { name: 'Ingresos', value: parseFloat(totalManualIncome.toFixed(2)), color: COLORS.success },
        { name: 'Egresos', value: parseFloat(totalExpenses.toFixed(2)), color: COLORS.danger }
      ].filter(item => item.value > 0);

      // 16. Ventas por Tipo (servir vs llevar)
      const servirCount = filteredSales.filter(s => s.order_type === 'servir').reduce((sum, s) => sum + s.total, 0);
      const llevarCount = filteredSales.filter(s => s.order_type === 'llevar').reduce((sum, s) => sum + s.total, 0);
      const ordersByType = [
        { name: 'Para Servir', value: servirCount, color: COLORS.primary },
        { name: 'Para Llevar', value: llevarCount, color: COLORS.orange }
      ].filter(item => item.value > 0);

      // 17. Distribucion de Montos (premium+)
      const ranges = [
        { label: '$0-3', min: 0, max: 3 },
        { label: '$3-5', min: 3, max: 5 },
        { label: '$5-10', min: 5, max: 10 },
        { label: '$10-20', min: 10, max: 20 },
        { label: '$20-50', min: 20, max: 50 },
        { label: '$50+', min: 50, max: Infinity }
      ];
      const salesDistribution = ranges.map(range => ({
        rango: range.label,
        cantidad: filteredSales.filter(s => s.total >= range.min && s.total < range.max).length
      }));

      // 18. Kitchen stats (premium+)
      let kitchenAvgByDay = [];
      let kitchenOnTimeRatio = [
        { name: 'A tiempo', value: 0, color: COLORS.success },
        { name: 'Con retraso', value: 0, color: COLORS.danger }
      ];
      let kitchenHourlyToday = [];
      let kitchenByWeekday = [];
      let kitchenByMonthDay = [];
      let kitchenTimingDetail = [];

      if (isPremiumOrHigher && kitchenData.length > 0) {
        // Filtrar kitchen data por periodo
        let filteredKitchen = kitchenData;
        if (timeFilter === 'today') {
          const today = getEcuadorDateStr(nowUtc.toISOString());
          filteredKitchen = kitchenData.filter(k => getEcuadorDateStr(k.created_at) === today);
        } else if (timeFilter === 'week') {
          const weekAgoUtc = new Date(nowUtc.getTime() - 7 * 24 * 60 * 60 * 1000);
          filteredKitchen = kitchenData.filter(k => toEcuadorTime(k.created_at) >= toEcuadorTime(weekAgoUtc.toISOString()));
        } else if (timeFilter === 'month') {
          const monthStartEcuador = new Date(now.getFullYear(), now.getMonth(), 1);
          filteredKitchen = kitchenData.filter(k => toEcuadorTime(k.created_at) >= monthStartEcuador);
        } else if (timeFilter === 'custom' && customDate) {
          filteredKitchen = kitchenData.filter(k => getEcuadorDateStr(k.created_at) === customDate);
        }

        // Kitchen avg by day
        const kitchenByDayMap = {};
        filteredKitchen.forEach(k => {
          const dateStr = getEcuadorDateStr(k.created_at);
          if (!kitchenByDayMap[dateStr]) kitchenByDayMap[dateStr] = { totalTime: 0, count: 0 };
          const startTime = new Date(k.kitchen_started_at);
          const endTime = new Date(k.kitchen_done_at);
          const prepMinutes = (endTime - startTime) / 60000;
          if (prepMinutes > 0 && prepMinutes < 180) {
            kitchenByDayMap[dateStr].totalTime += prepMinutes;
            kitchenByDayMap[dateStr].count += 1;
          }
        });

        kitchenAvgByDay = Object.entries(kitchenByDayMap)
          .sort(([a], [b]) => a.localeCompare(b))
          .slice(-7)
          .map(([date, data]) => ({
            date: new Date(date + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
            promedio: data.count > 0 ? parseFloat((data.totalTime / data.count).toFixed(1)) : 0
          }));

        // On-time vs late
        const onTimeCount = filteredKitchen.filter(k => (k.kitchen_overtime_seconds || 0) === 0).length;
        const lateCount = filteredKitchen.filter(k => (k.kitchen_overtime_seconds || 0) > 0).length;
        kitchenOnTimeRatio = [
          { name: 'A tiempo', value: onTimeCount, color: COLORS.success },
          { name: 'Con retraso', value: lateCount, color: COLORS.danger }
        ].filter(item => item.value > 0);

        // Helper comun: minutos reales de preparacion de una comanda (con filtro de outliers)
        const withRealMinutes = (list) => list
          .map(k => {
            const startTime = new Date(k.kitchen_started_at);
            const endTime = new Date(k.kitchen_done_at);
            return { ...k, realMinutes: (endTime - startTime) / 60000 };
          })
          .filter(k => k.realMinutes > 0 && k.realMinutes < 180);

        // 19. Tiempo promedio de salida por HORA, del dia de hoy (premium+)
        const today = getEcuadorDateStr(nowUtc.toISOString());
        const todayKitchen = withRealMinutes(
          kitchenData.filter(k => getEcuadorDateStr(k.kitchen_done_at || k.created_at) === today)
        );
        const hourlyMap = {};
        todayKitchen.forEach(k => {
          const hour = toEcuadorTime(k.kitchen_done_at).getHours();
          if (!hourlyMap[hour]) hourlyMap[hour] = { totalTime: 0, count: 0 };
          hourlyMap[hour].totalTime += k.realMinutes;
          hourlyMap[hour].count += 1;
        });
        kitchenHourlyToday = Object.entries(hourlyMap)
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([hour, data]) => ({
            hour: `${hour}:00`,
            promedio: parseFloat((data.totalTime / data.count).toFixed(1))
          }));

        // 20. Tiempo promedio por DIA DE LA SEMANA (historico completo, premium+)
        const weekdayMap = {};
        for (let i = 0; i < 7; i++) weekdayMap[i] = { totalTime: 0, count: 0 };
        withRealMinutes(kitchenData).forEach(k => {
          const dow = toEcuadorTime(k.kitchen_done_at).getDay();
          weekdayMap[dow].totalTime += k.realMinutes;
          weekdayMap[dow].count += 1;
        });
        kitchenByWeekday = Object.entries(weekdayMap).map(([dow, data]) => ({
          dia: WEEKDAY_NAMES[dow],
          promedio: data.count > 0 ? parseFloat((data.totalTime / data.count).toFixed(1)) : 0,
          pedidos: data.count
        }));

        // 21. Tiempo promedio por DIA DEL MES ACTUAL (premium+)
        const monthDayMap = {};
        withRealMinutes(kitchenData)
          .filter(k => {
            const d = toEcuadorTime(k.kitchen_done_at);
            return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
          })
          .forEach(k => {
            const dayOfMonth = toEcuadorTime(k.kitchen_done_at).getDate();
            if (!monthDayMap[dayOfMonth]) monthDayMap[dayOfMonth] = { totalTime: 0, count: 0 };
            monthDayMap[dayOfMonth].totalTime += k.realMinutes;
            monthDayMap[dayOfMonth].count += 1;
          });
        kitchenByMonthDay = Object.entries(monthDayMap)
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([day, data]) => ({
            dia: day,
            promedio: parseFloat((data.totalTime / data.count).toFixed(1))
          }));

        // 22. Detalle real vs estimado por pedido, respetando el filtro de periodo activo (premium+)
        kitchenTimingDetail = withRealMinutes(filteredKitchen)
          .map(k => {
            const estimado = k.kitchen_estimated_minutes;
            const diferencia = estimado != null ? parseFloat((k.realMinutes - estimado).toFixed(1)) : null;
            return {
              id: k.id,
              hora: formatEcuadorTime(k.kitchen_done_at),
              real: parseFloat(k.realMinutes.toFixed(1)),
              estimado: estimado != null ? parseFloat(estimado.toFixed(1)) : null,
              diferencia
            };
          })
          .sort((a, b) => b.id - a.id);
      }

      setDashboardData({
        totalSales, totalExpenses, totalManualIncome, totalOrders, balance,
        bestProduct, cashVsTransfer, salesByDay, topProducts, bottomProducts,
        salesTrend, salesByHour, salesByCategory, monthlyComparison,
        incomeVsExpenses, ordersByType, orderCountByDay, avgTicketByDay,
        salesDistribution, kitchenAvgByDay, kitchenOnTimeRatio,
        kitchenHourlyToday, kitchenByWeekday, kitchenByMonthDay, kitchenTimingDetail
      });
    } catch (error) {
      console.error('Error al cargar datos del dashboard:', error);
      showAlert('error', 'Error', 'No se pudieron cargar los datos del dashboard');
    } finally {
      setLoadingDashboard(false);
    }
  };

  // ============================================
  // TAB 2: VENTAS
  // ============================================
  const loadSales = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pos/sales`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allData = response.data;
      const filtered = allData.filter(sale => getEcuadorDateStr(sale.created_at) === selectedDate);
      setSales(filtered);

      // ⭐ Mismo fix que loadDashboardData: solo lo YA cobrado ('paid') cuenta
      // como venta del día -- los 'pending' se siguen listando abajo (con su
      // badge "PENDIENTE DE PAGO"), pero no suman en estas tarjetas.
      const total_sales = filtered.filter(s => !s.cancelled && s.payment_status === 'paid').length;
      const total_revenue = filtered.filter(s => !s.cancelled && s.payment_status === 'paid').reduce((sum, sale) => sum + sale.total, 0);
      setStats({ total_sales, total_revenue });
    } catch (error) {
      console.error('Error al cargar ventas:', error);
    }
  };

  // ⭐ Historial de cierres de caja (quién cerró, cuándo, diferencia) —
  // reutiliza el endpoint que ya existe para CierreCaja.js, sin cambios
  // de backend adicionales; aquí solo se consume para mostrarlo en
  // Reportes tal como pidió el cliente ("por quien, a q hora").
  const loadCashSessions = async () => {
    setLoadingCashSessions(true);
    try {
      const response = await axios.get(`${API_URL}/api/pos/cash-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCashSessions(response.data.filter(s => s.status === 'closed'));
    } catch (error) {
      console.error('Error al cargar historial de cierres de caja:', error);
    } finally {
      setLoadingCashSessions(false);
    }
  };

  const handleCancelSale = (saleId) => {
    setCancelModal({ saleId, reason: '' });
  };

  const confirmCancelSale = async () => {
    if (!cancelModal) return;
    const { saleId, reason } = cancelModal;
    if (!reason.trim()) {
      showAlert('warning', 'Motivo Requerido', 'Debes ingresar el motivo de anulacion');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/pos/sales/${saleId}/cancel`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCancelModal(null);
      showAlert('success', 'Venta Anulada', `Venta anulada exitosamente.\nMotivo: ${reason}\nEl stock ha sido restaurado.`);
      loadSales();
    } catch (error) {
      console.error('Error:', error);
      showAlert('error', 'Error', `${error.response?.data?.error || 'No se pudo anular la venta'}`);
    }
  };

  // ⭐ Edición de pedido ya pagado (ver comentario junto al estado más
  // arriba). openEditSale clona los items del pedido seleccionado para no
  // mutar selectedSale mientras se edita -- si el usuario cancela, el
  // pedido original queda intacto.
  const openEditSale = () => {
    if (!selectedSale) return;
    setEditingSaleItems((selectedSale.items || []).map(it => ({ ...it })));
    setEditSaleReason('');
  };

  const updateEditSaleItemQty = (idx, delta) => {
    setEditingSaleItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const newQty = Math.max(0, (Number(it.quantity) || 0) + delta);
      return { ...it, quantity: newQty };
    }).filter(it => it.quantity > 0));
  };

  const editingSaleTotal = (editingSaleItems || []).reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);

  const handleSaveSaleEdit = async () => {
    if (!selectedSale || !editingSaleItems) return;
    if (!editSaleReason.trim()) {
      showAlert('warning', 'Motivo Requerido', 'Explica por qué se está editando este pedido ya pagado (queda en Auditoría).');
      return;
    }
    if (editingSaleItems.length === 0) {
      showAlert('warning', 'Pedido vacío', 'Un pedido editado no puede quedar sin productos. Si quieres anularlo, usa "Anular Venta".');
      return;
    }
    setSavingSaleEdit(true);
    try {
      await axios.put(
        `${API_URL}/api/pos/sales/${selectedSale.id}/edit-paid`,
        { items: editingSaleItems, reason: editSaleReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert('success', 'Pedido editado', 'El pedido pagado fue actualizado. El cambio quedó registrado en Auditoría.');
      setEditingSaleItems(null);
      setShowSaleModal(false);
      loadSales();
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo editar el pedido');
    } finally {
      setSavingSaleEdit(false);
    }
  };

  // ============================================
  // PAGOS MANUALES (integrados en Ventas)
  // ============================================
  const loadPayments = async () => {
    setLoadingPayments(true);
    try {
      const response = await axios.get(`${API_URL}/api/pos/manual-payments`, {
        headers: { Authorization: `Bearer ${token}` },
        params: filters
      });
      setPayments(response.data);
    } catch (error) {
      console.error('Error al cargar pagos:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      showAlert('warning', 'Campo Requerido', 'Ingresa una descripcion');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      showAlert('warning', 'Monto Invalido', 'El monto debe ser mayor a 0');
      return;
    }
    setLoadingPayments(true);
    try {
      if (editingPayment) {
        await axios.put(
          `${API_URL}/api/pos/manual-payments/${editingPayment.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showAlert('success', 'Pago Actualizado', 'Cambios guardados exitosamente');
      } else {
        await axios.post(
          `${API_URL}/api/pos/manual-payments`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showAlert('success', 'Pago Registrado', 'Pago guardado exitosamente');
      }
      handleClosePaymentModal();
      loadPayments();
    } catch (error) {
      console.error('Error al guardar pago:', error);
      showAlert('error', 'Error', `${error.response?.data?.error || 'No se pudo guardar el pago'}`);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setFormData({
      type: payment.type,
      category: payment.category,
      description: payment.description,
      amount: payment.amount.toString(),
      payment_method: payment.payment_method,
      reference: payment.reference || '',
      notes: payment.notes || '',
      date: payment.date.split('T')[0]
    });
    setShowPaymentModal(true);
  };

  const handleDeletePayment = (id) => {
    setConfirmDeletePayment(id);
  };

  const executeDeletePayment = async (id) => {
    setConfirmDeletePayment(null);
    try {
      await axios.delete(`${API_URL}/api/pos/manual-payments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showAlert('success', 'Pago Eliminado', 'Pago eliminado exitosamente');
      loadPayments();
    } catch (error) {
      console.error('Error al eliminar:', error);
      showAlert('error', 'Error', 'No se pudo eliminar el pago');
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setEditingPayment(null);
    setFormData({
      type: 'expense',
      category: 'purchase',
      description: '',
      amount: '',
      payment_method: 'cash',
      reference: '',
      notes: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  // ============================================
  // EXPORTAR REPORTE DEL DIA (XLSX ESTILIZADO)
  // ============================================
  const paymentMethodLabel = (method) => {
    if (method === 'cash') return 'Efectivo';
    if (method === 'transfer') return 'Transferencia';
    if (method === 'mixed') return 'Mixto';
    if (method === 'card') return 'Tarjeta';
    return method || '-';
  };

  const styleHeaderRow = (row, argbColor) => {
    row.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argbColor } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        right: { style: 'thin', color: { argb: 'FFD1D5DB' } }
      };
    });
    row.height = 22;
  };

  const thinBorderAll = { style: 'thin', color: { argb: 'FFE5E7EB' } };
  const applyRowBorder = (row) => {
    row.eachCell((cell) => {
      cell.border = { top: thinBorderAll, bottom: thinBorderAll, left: thinBorderAll, right: thinBorderAll };
    });
  };

  const exportToXLSX = async () => {
    if (exportingReport) return;
    setExportingReport(true);
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Gestor SaaS POS';
      workbook.created = new Date();

      const businessName = clientData?.business_name || clientData?.name || 'Mi Negocio';
      const dayTotals = getDayPaymentTotals();
      const dayBalance = stats.total_revenue + dayTotals.income - dayTotals.expenses;
      const fechaFormateada = new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-ES', {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
      });

      // ⭐ Mismo criterio que las tarjetas del dashboard: el reporte exportado
      // no debe incluir pedidos 'pending' como si ya fueran venta cobrada.
      const nonCancelledSales = sales.filter(s => !s.cancelled && s.payment_status === 'paid');
      const cashTotal = nonCancelledSales.filter(s => s.payment_method === 'cash').reduce((sum, s) => sum + s.total, 0);
      const transferTotal = nonCancelledSales.filter(s => s.payment_method === 'transfer').reduce((sum, s) => sum + s.total, 0);
      const mixedTotal = nonCancelledSales.filter(s => s.payment_method === 'mixed').reduce((sum, s) => sum + s.total, 0);

      // ---------------- HOJA 1: RESUMEN ----------------
      const resumen = workbook.addWorksheet('Resumen', { views: [{ showGridLines: false }] });
      resumen.columns = [{ width: 3 }, { width: 30 }, { width: 22 }, { width: 3 }];

      resumen.mergeCells('B2:C2');
      const titleCell = resumen.getCell('B2');
      titleCell.value = businessName;
      titleCell.font = { size: 20, bold: true, color: { argb: 'FF1E3A8A' } };

      resumen.mergeCells('B3:C3');
      const subtitleCell = resumen.getCell('B3');
      subtitleCell.value = `Reporte Diario  —  ${fechaFormateada}`;
      subtitleCell.font = { size: 12, italic: true, color: { argb: 'FF6B7280' } };

      resumen.mergeCells('B5:C5');
      const kpiHeaderCell = resumen.getCell('B5');
      kpiHeaderCell.value = 'RESUMEN FINANCIERO';
      kpiHeaderCell.font = { bold: true, size: 12, color: { argb: 'FF374151' } };

      const kpis = [
        { label: `Ventas del Día  (${stats.total_sales} pedidos)`, value: stats.total_revenue, color: 'FF3B82F6' },
        { label: 'Egresos', value: dayTotals.expenses, color: 'FFEF4444' },
        { label: 'Ingresos Manuales', value: dayTotals.income, color: 'FF10B981' },
        { label: 'Balance del Día', value: dayBalance, color: dayBalance >= 0 ? 'FF14B8A6' : 'FFF97316' }
      ];

      let rIdx = 6;
      kpis.forEach((kpi) => {
        const labelCell = resumen.getCell(`B${rIdx}`);
        const valueCell = resumen.getCell(`C${rIdx}`);
        labelCell.value = kpi.label;
        labelCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: kpi.color } };
        labelCell.alignment = { vertical: 'middle', indent: 1 };
        valueCell.value = kpi.value;
        valueCell.numFmt = '"$"#,##0.00';
        valueCell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
        valueCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: kpi.color } };
        valueCell.alignment = { vertical: 'middle', horizontal: 'right', indent: 1 };
        resumen.getRow(rIdx).height = 24;
        rIdx += 1;
      });

      rIdx += 1;
      resumen.mergeCells(`B${rIdx}:C${rIdx}`);
      const metodosHeaderCell = resumen.getCell(`B${rIdx}`);
      metodosHeaderCell.value = 'VENTAS POR MÉTODO DE PAGO';
      metodosHeaderCell.font = { bold: true, size: 12, color: { argb: 'FF374151' } };
      rIdx += 1;

      const metodos = [
        { label: 'Efectivo', value: cashTotal, color: 'FFD1FAE5' },
        { label: 'Transferencia', value: transferTotal, color: 'FFDBEAFE' },
        { label: 'Mixto', value: mixedTotal, color: 'FFFEF3C7' }
      ];
      metodos.forEach((m) => {
        const labelCell = resumen.getCell(`B${rIdx}`);
        const valueCell = resumen.getCell(`C${rIdx}`);
        labelCell.value = m.label;
        labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: m.color } };
        labelCell.alignment = { indent: 1 };
        valueCell.value = m.value;
        valueCell.numFmt = '"$"#,##0.00';
        valueCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: m.color } };
        valueCell.alignment = { horizontal: 'right', indent: 1 };
        rIdx += 1;
      });

      // ---------------- HOJA 2: VENTAS DEL DIA ----------------
      const ventasSheet = workbook.addWorksheet('Ventas del Día', { views: [{ state: 'frozen', ySplit: 1 }] });
      ventasSheet.columns = [
        { header: 'Código', key: 'codigo', width: 14 },
        { header: 'Hora', key: 'hora', width: 10 },
        { header: 'Estado', key: 'estado', width: 12 },
        { header: 'Mesa / Tipo', key: 'mesa', width: 18 },
        { header: 'Método de Pago', key: 'metodo', width: 16 },
        { header: 'Productos', key: 'productos', width: 45 },
        { header: 'Total', key: 'total', width: 14 }
      ];
      styleHeaderRow(ventasSheet.getRow(1), 'FF1D4ED8');

      sales.forEach((sale) => {
        const mesaTexto = (sale.table_name || sale.table_id)
          ? `Mesa ${sale.table_name || sale.table_id}`
          : (sale.order_type === 'llevar' ? 'Para Llevar' : 'Para Servir');
        const productosTexto = (sale.items || []).map(it => `${it.quantity}x ${it.name}`).join(', ');
        const row = ventasSheet.addRow({
          codigo: getDayCode(sale, sales),
          hora: formatEcuadorTime(sale.created_at),
          estado: sale.cancelled ? 'ANULADA' : 'Pagado',
          mesa: mesaTexto,
          metodo: paymentMethodLabel(sale.payment_method),
          productos: productosTexto,
          total: sale.total
        });
        row.getCell('total').numFmt = '"$"#,##0.00';
        applyRowBorder(row);
        if (sale.cancelled) {
          row.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } };
            cell.font = { color: { argb: 'FF991B1B' }, strike: true };
          });
        } else if (row.number % 2 === 0) {
          row.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
          });
        }
      });

      if (sales.length === 0) {
        ventasSheet.addRow({ codigo: 'No hay ventas registradas para esta fecha' });
      } else {
        const totalRow = ventasSheet.addRow({
          productos: 'TOTAL VENTAS (sin anuladas)',
          total: nonCancelledSales.reduce((sum, s) => sum + s.total, 0)
        });
        totalRow.getCell('total').numFmt = '"$"#,##0.00';
        totalRow.eachCell((cell) => {
          cell.font = { bold: true };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } };
          cell.border = { top: { style: 'double', color: { argb: 'FF1D4ED8' } } };
        });
      }

      // ---------------- HOJA 3: INGRESOS Y EGRESOS ----------------
      const pagosSheet = workbook.addWorksheet('Ingresos y Egresos', { views: [{ state: 'frozen', ySplit: 1 }] });
      pagosSheet.columns = [
        { header: 'Fecha', key: 'fecha', width: 14 },
        { header: 'Tipo', key: 'tipo', width: 12 },
        { header: 'Categoría', key: 'categoria', width: 22 },
        { header: 'Descripción', key: 'descripcion', width: 35 },
        { header: 'Monto', key: 'monto', width: 14 },
        { header: 'Método de Pago', key: 'metodo', width: 16 },
        { header: 'Referencia', key: 'referencia', width: 18 }
      ];
      styleHeaderRow(pagosSheet.getRow(1), 'FF047857');

      dayTotals.payments.forEach((p) => {
        const row = pagosSheet.addRow({
          fecha: new Date(p.date).toLocaleDateString('es-ES'),
          tipo: p.type === 'income' ? 'Ingreso' : 'Egreso',
          categoria: getCategoryLabel(p.type, p.category),
          descripcion: p.description,
          monto: p.amount,
          metodo: paymentMethodLabel(p.payment_method),
          referencia: p.reference || ''
        });
        row.getCell('monto').numFmt = '"$"#,##0.00';
        applyRowBorder(row);
        row.eachCell((cell) => {
          cell.fill = {
            type: 'pattern', pattern: 'solid',
            fgColor: { argb: p.type === 'income' ? 'FFECFDF5' : 'FFFEF2F2' }
          };
        });
        row.getCell('tipo').font = { bold: true, color: { argb: p.type === 'income' ? 'FF047857' : 'FFB91C1C' } };
      });

      if (dayTotals.payments.length === 0) {
        pagosSheet.addRow({ fecha: 'No hay ingresos ni egresos manuales registrados para esta fecha' });
      } else {
        const totalPagosRow = pagosSheet.addRow({
          descripcion: 'TOTAL INGRESOS / EGRESOS',
          monto: dayTotals.income - dayTotals.expenses
        });
        totalPagosRow.getCell('monto').numFmt = '"$"#,##0.00';
        totalPagosRow.eachCell((cell) => {
          cell.font = { bold: true };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } };
          cell.border = { top: { style: 'double', color: { argb: 'FF047857' } } };
        });
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte-${selectedDate}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al generar el reporte XLSX:', error);
      showAlert('error', 'Error', 'No se pudo generar el reporte. Intenta nuevamente.');
    } finally {
      setExportingReport(false);
    }
  };

  const getCategoryLabel = (type, category) => {
    const list = categories[type] || [];
    const found = list.find(c => c.value === category);
    return found ? found.label : category;
  };

  const getTypeColor = (type) => {
    return type === 'income' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100';
  };

  // Totales de pagos del dia seleccionado
  const getDayPaymentTotals = () => {
    const dayPayments = payments.filter(p => (p.date || '').split('T')[0] === selectedDate);
    const income = dayPayments.filter(p => p.type === 'income').reduce((sum, p) => sum + p.amount, 0);
    const expenses = dayPayments.filter(p => p.type === 'expense').reduce((sum, p) => sum + p.amount, 0);
    return { income, expenses, balance: income - expenses, payments: dayPayments };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'USD' }).format(value);
  };

  // Etiqueta corta del periodo activo (para el badge de la tarjeta)
  const getPeriodBadge = () => {
    if (timeFilter === 'today') return 'Hoy';
    if (timeFilter === 'week') return 'Semana';
    if (timeFilter === 'month') return 'Mes';
    if (timeFilter === 'custom' && customDate) {
      return new Date(customDate + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    return '';
  };

  // Titulo dinamico de la tarjeta "Ventas Totales" segun el periodo seleccionado
  const getVentasTotalesTitle = () => {
    if (timeFilter === 'today') return 'Ventas Totales del Dia';
    if (timeFilter === 'week') return 'Ventas Totales de la Semana';
    if (timeFilter === 'month') return 'Ventas Totales del Mes';
    if (timeFilter === 'custom' && customDate) {
      return `Ventas Totales del ${getPeriodBadge()}`;
    }
    return 'Ventas Totales';
  };

  // Componente reutilizable para seccion de grafica
  const ChartSection = ({ title, badge, children, isEmpty, emptyMessage }) => (
    <div className="bg-white rounded-lg shadow-md p-6 relative">
      {badge && (
        <div className="absolute top-4 right-4">
          <span className={`${badge === 'PREMIUM' ? 'bg-gradient-to-r from-yellow-400 to-orange-500' : 'bg-gradient-to-r from-blue-400 to-blue-600'} text-white px-3 py-1 rounded-full text-xs font-bold`}>
            {badge}
          </span>
        </div>
      )}
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      {isEmpty ? (
        <p className="text-center text-gray-500 py-12">{emptyMessage || 'No hay datos'}</p>
      ) : children}
    </div>
  );

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="space-y-6">
      {customAlert && (
        <CustomAlert
          type={customAlert.type}
          title={customAlert.title}
          message={customAlert.message}
          onClose={() => setCustomAlert(null)}
          zIndex={70}
        />
      )}

      {confirmDeletePayment !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Eliminar pago</h3>
            <p className="text-gray-600 text-sm mb-6">Eliminar este registro de pago? Esta accion no se puede deshacer.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDeletePayment(null)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancelar</button>
              <button onClick={() => executeDeletePayment(confirmDeletePayment)} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold">Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ANULACION CON MOTIVO */}
      {cancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-rose-600 p-5">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                Anular Venta #{cancelModal.saleId}
              </h3>
              <p className="text-red-100 text-sm mt-1">Esta accion restaurara el stock y no se puede deshacer</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">El stock de los productos se devolvera automaticamente (si aplica)</p>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Motivo de anulacion <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cancelModal.reason}
                  onChange={(e) => setCancelModal({ ...cancelModal, reason: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                  rows={3}
                  placeholder="Ej: Error en el pedido, cliente cancelo, duplicado..."
                  autoFocus
                />
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t bg-gray-50">
              <button onClick={() => setCancelModal(null)} className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300 transition">
                Cancelar
              </button>
              <button onClick={confirmCancelSale} className="flex-1 px-4 py-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition flex items-center justify-center gap-2">
                Anular Venta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TABS - Solo Dashboard y Ventas */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex-1 px-6 py-4 font-semibold text-sm transition ${activeTab === 'dashboard'
                ? 'bg-blue-600 text-white border-b-2 border-blue-600'
                : 'text-gray-600 hover:bg-gray-50'
              }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('ventas')}
            className={`flex-1 px-6 py-4 font-semibold text-sm transition ${activeTab === 'ventas'
                ? 'bg-blue-600 text-white border-b-2 border-blue-600'
                : 'text-gray-600 hover:bg-gray-50'
              }`}
          >
            Ventas y Gastos
          </button>
        </div>
      </div>

      {/* ============================================ */}
      {/* TAB 1: DASHBOARD */}
      {/* ============================================ */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Filtros de Tiempo */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-4">
              <Calendar className="text-blue-600" size={24} />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Periodo de Analisis</h3>
                <div className="flex gap-3 flex-wrap items-center">
                  {['today', 'week', 'month'].map(filter => (
                    <button
                      key={filter}
                      onClick={() => { setTimeFilter(filter); setCustomDate(''); }}
                      className={`px-6 py-2 rounded-lg font-semibold transition ${timeFilter === filter
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      {filter === 'today' ? 'Hoy' : filter === 'week' ? 'Esta Semana' : 'Este Mes'}
                    </button>
                  ))}
                  <span className="relative inline-flex">
                    <button
                      type="button"
                      onClick={openCustomDatePicker}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition cursor-pointer ${timeFilter === 'custom'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      <Calendar size={18} />
                      <span>{timeFilter === 'custom' && customDate ? getPeriodBadge() : 'Elegir Fecha'}</span>
                    </button>
                    <input
                      ref={customDateInputRef}
                      type="date"
                      value={customDate}
                      onChange={(e) => { setCustomDate(e.target.value); setTimeFilter('custom'); }}
                      className="sr-only"
                      tabIndex={-1}
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>

          {loadingDashboard ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Cargando datos...</p>
            </div>
          ) : (
            <>
              {/* TARJETAS FINANCIERAS */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Ventas Totales */}
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md p-4 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingUp size={20} className="opacity-80" />
                    <span className="text-xs bg-blue-400 px-2 py-0.5 rounded-full">
                      {getPeriodBadge()}
                    </span>
                  </div>
                  <p className="text-blue-100 text-xs font-medium">{getVentasTotalesTitle()}</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(dashboardData.totalSales)}</p>
                </div>

                {/* Egresos */}
                <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-md p-4 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <ArrowDownRight size={20} className="opacity-80" />
                  </div>
                  <p className="text-red-100 text-xs font-medium">Egresos</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(dashboardData.totalExpenses)}</p>
                </div>

                {/* Balance */}
                <div className={`bg-gradient-to-br ${dashboardData.balance >= 0 ? 'from-teal-500 to-teal-600' : 'from-orange-500 to-orange-600'} rounded-lg shadow-md p-4 text-white`}>
                  <div className="flex items-center justify-between mb-2">
                    <DollarSign size={20} className="opacity-80" />
                  </div>
                  <p className="text-white text-xs font-medium opacity-80">Balance</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(dashboardData.balance)}</p>
                </div>

                {/* Ordenes */}
                <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow-md p-4 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <ShoppingBag size={20} className="opacity-80" />
                  </div>
                  <p className="text-indigo-100 text-xs font-medium">Total Ordenes</p>
                  <p className="text-2xl font-bold mt-1">{dashboardData.totalOrders}</p>
                </div>
              </div>

              {(() => {
                // Definicion de todas las graficas disponibles (id estable + plan minimo requerido)
                const chartDefs = [
                  {
                    id: 'metodos-pago', minPlan: 'all', node: (
                      <ChartSection title="Metodos de Pago" isEmpty={dashboardData.cashVsTransfer.length === 0}>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={dashboardData.cashVsTransfer}
                              cx="50%" cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                              outerRadius={80} fill="#8884d8" dataKey="value"
                            >
                              {dashboardData.cashVsTransfer.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'ventas-dia', minPlan: 'all', node: (
                      <ChartSection title="Ventas por Dia ($)" isEmpty={dashboardData.salesByDay.length === 0}>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={dashboardData.salesByDay}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Bar dataKey="total" fill={COLORS.primary} name="Ventas" />
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'top-productos-pizza', minPlan: 'all', node: (
                      <ChartSection title={`Productos Mas Vendidos (${getPeriodBadge()})`} isEmpty={dashboardData.topProducts.length === 0}>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={dashboardData.topProducts}
                              cx="50%" cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                              outerRadius={100} fill="#8884d8" dataKey="quantity"
                            >
                              {dashboardData.topProducts.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value, name) => [`${value} vendidos`, name]} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'bottom-productos', minPlan: 'all', node: (
                      <ChartSection title="Top 5 Productos Menos Vendidos" isEmpty={dashboardData.bottomProducts.length === 0}>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={dashboardData.bottomProducts} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" width={150} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="quantity" fill={COLORS.danger} name="Cantidad Vendida" />
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'ingresos-egresos', minPlan: 'all', node: (
                      <ChartSection title="Ingresos vs Egresos" isEmpty={dashboardData.incomeVsExpenses.length === 0}>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={dashboardData.incomeVsExpenses}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Bar dataKey="value" name="Monto">
                              {dashboardData.incomeVsExpenses.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'ventas-tipo-pedido', minPlan: 'all', node: (
                      <ChartSection title="Ventas por Tipo de Pedido" isEmpty={dashboardData.ordersByType.length === 0}>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={dashboardData.ordersByType}
                              cx="50%" cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                              outerRadius={80} fill="#8884d8" dataKey="value"
                            >
                              {dashboardData.ordersByType.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'ordenes-por-dia', minPlan: 'all', node: (
                      <ChartSection title="Numero de Ordenes por Dia" isEmpty={dashboardData.orderCountByDay.length === 0}>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={dashboardData.orderCountByDay}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="ordenes" stroke={COLORS.indigo} name="Ordenes" strokeWidth={2} dot={{ r: 4 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'tendencia-ventas', minPlan: 'estandar', node: (
                      <ChartSection title="Tendencia de Ventas (30 dias)" badge="ESTANDAR+" isEmpty={dashboardData.salesTrend.length === 0}>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={dashboardData.salesTrend}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Line type="monotone" dataKey="total" stroke={COLORS.purple} name="Ventas" strokeWidth={2} />
                          </LineChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'ventas-hora', minPlan: 'estandar', node: (
                      <ChartSection title="Ventas por Hora" badge="ESTANDAR+" isEmpty={dashboardData.salesByHour.length === 0}>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={dashboardData.salesByHour}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="hour" />
                            <YAxis />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Bar dataKey="total" fill={COLORS.warning} name="Ventas" />
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'ventas-categoria', minPlan: 'estandar', node: (
                      <ChartSection title="Ventas por Categoria" badge="ESTANDAR+" isEmpty={dashboardData.salesByCategory.length === 0}>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={dashboardData.salesByCategory}
                              cx="50%" cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                              outerRadius={80} fill="#8884d8" dataKey="value"
                            >
                              {dashboardData.salesByCategory.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'comparativa-mensual', minPlan: 'estandar', node: (
                      <ChartSection title="Comparativa Mensual" badge="ESTANDAR+" isEmpty={dashboardData.monthlyComparison.length === 0}>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={dashboardData.monthlyComparison}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Bar dataKey="ventas" fill={COLORS.pink} name="Ventas" />
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'tiempo-cocina', minPlan: 'premium', node: (
                      <ChartSection
                        title="Tiempo Promedio de Preparacion (min)"
                        badge="PREMIUM"
                        isEmpty={dashboardData.kitchenAvgByDay.length === 0}
                        emptyMessage="No hay datos de tiempos de cocina. Marca pedidos como listos en Cocina para generar estadisticas."
                      >
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={dashboardData.kitchenAvgByDay}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis unit=" min" />
                            <Tooltip formatter={(value) => `${value} min`} />
                            <Legend />
                            <Bar dataKey="promedio" fill={COLORS.teal} name="Promedio (min)" />
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'tiempo-cocina-hora', minPlan: 'premium', node: (
                      <ChartSection
                        title="Tiempo Promedio de Salida por Hora (Hoy)"
                        badge="PREMIUM"
                        isEmpty={dashboardData.kitchenHourlyToday.length === 0}
                        emptyMessage="Aun no hay comandas marcadas como listas hoy."
                      >
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={dashboardData.kitchenHourlyToday}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="hour" />
                            <YAxis unit=" min" />
                            <Tooltip formatter={(value) => `${value} min`} />
                            <Legend />
                            <Bar dataKey="promedio" fill={COLORS.orange} name="Promedio (min)" />
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'tiempo-cocina-semana', minPlan: 'premium', node: (
                      <ChartSection
                        title="Tiempo Promedio de Salida por Dia de la Semana"
                        badge="PREMIUM"
                        isEmpty={dashboardData.kitchenByWeekday.every(d => d.pedidos === 0)}
                        emptyMessage="Aun no hay suficiente historial de cocina."
                      >
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={dashboardData.kitchenByWeekday}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="dia" />
                            <YAxis unit=" min" />
                            <Tooltip formatter={(value, name) => name === 'promedio' ? [`${value} min`, 'Promedio'] : [value, name]} />
                            <Legend />
                            <Bar dataKey="promedio" fill={COLORS.indigo} name="Promedio (min)" />
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'tiempo-cocina-mes', minPlan: 'premium', node: (
                      <ChartSection
                        title="Tiempo Promedio de Salida por Dia del Mes Actual"
                        badge="PREMIUM"
                        isEmpty={dashboardData.kitchenByMonthDay.length === 0}
                        emptyMessage="Aun no hay comandas completadas este mes."
                      >
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={dashboardData.kitchenByMonthDay}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="dia" />
                            <YAxis unit=" min" />
                            <Tooltip formatter={(value) => `${value} min`} />
                            <Legend />
                            <Line type="monotone" dataKey="promedio" stroke={COLORS.pink} name="Promedio (min)" strokeWidth={2} dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'tiempo-cocina-detalle', minPlan: 'premium', node: (
                      <ChartSection
                        title={`Tiempo Real vs Estimado por Pedido (${getPeriodBadge()})`}
                        badge="PREMIUM"
                        isEmpty={dashboardData.kitchenTimingDetail.length === 0}
                        emptyMessage="Aun no hay comandas completadas en este periodo."
                      >
                        <div className="overflow-x-auto max-h-80 overflow-y-auto">
                          <table className="min-w-full text-sm">
                            <thead className="sticky top-0 bg-white">
                              <tr className="text-left text-gray-500 border-b">
                                <th className="py-2 pr-4">Pedido</th>
                                <th className="py-2 pr-4">Hora</th>
                                <th className="py-2 pr-4">T. Real</th>
                                <th className="py-2 pr-4">T. Estimado</th>
                                <th className="py-2 pr-4">Diferencia</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dashboardData.kitchenTimingDetail.map(row => (
                                <tr key={row.id} className="border-b border-gray-100">
                                  <td className="py-2 pr-4 font-semibold text-gray-700">#{row.id}</td>
                                  <td className="py-2 pr-4 text-gray-500">{row.hora}</td>
                                  <td className="py-2 pr-4">{row.real} min</td>
                                  <td className="py-2 pr-4">{row.estimado != null ? `${row.estimado} min` : 'N/D'}</td>
                                  <td className="py-2 pr-4">
                                    {row.diferencia == null ? (
                                      <span className="text-gray-400">N/D</span>
                                    ) : row.diferencia > 0 ? (
                                      <span className="text-red-600 font-semibold">+{row.diferencia} min tarde</span>
                                    ) : row.diferencia < 0 ? (
                                      <span className="text-green-600 font-semibold">{row.diferencia} min antes</span>
                                    ) : (
                                      <span className="text-blue-600 font-semibold">Exacto</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="text-xs text-gray-400 mt-3">
                          "N/D" = pedido completado antes de esta actualizacion (aun no tiene tiempo estimado guardado).
                        </p>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'ordenes-tiempo', minPlan: 'premium', node: (
                      <ChartSection
                        title="Ordenes a Tiempo vs Con Retraso"
                        badge="PREMIUM"
                        isEmpty={dashboardData.kitchenOnTimeRatio.every(d => d.value === 0)}
                        emptyMessage="No hay datos de tiempos de cocina aun."
                      >
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={dashboardData.kitchenOnTimeRatio}
                              cx="50%" cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                              outerRadius={80} fill="#8884d8" dataKey="value"
                            >
                              {dashboardData.kitchenOnTimeRatio.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'ticket-promedio', minPlan: 'premium', node: (
                      <ChartSection title="Ticket Promedio por Dia" badge="PREMIUM" isEmpty={dashboardData.avgTicketByDay.length === 0}>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={dashboardData.avgTicketByDay}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Line type="monotone" dataKey="promedio" stroke={COLORS.cyan} name="Ticket Promedio" strokeWidth={2} dot={{ r: 4 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                  {
                    id: 'distribucion-montos', minPlan: 'premium', node: (
                      <ChartSection title="Distribucion de Montos de Venta" badge="PREMIUM" isEmpty={dashboardData.salesDistribution.every(d => d.cantidad === 0)}>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={dashboardData.salesDistribution}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="rango" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="cantidad" fill={COLORS.purple} name="Cantidad de Ordenes" />
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartSection>
                    )
                  },
                ];

                const visibleCharts = chartDefs.filter(c => {
                  if (c.minPlan === 'estandar') return isStandardOrHigher;
                  if (c.minPlan === 'premium') return isPremiumOrHigher;
                  return true;
                });

                const visibleIds = new Set(visibleCharts.map(c => c.id));
                const orderedIds = [
                  ...chartOrder.filter(id => visibleIds.has(id)),
                  ...visibleCharts.map(c => c.id).filter(id => !chartOrder.includes(id))
                ];
                const orderedCharts = orderedIds
                  .map(id => visibleCharts.find(c => c.id === id))
                  .filter(Boolean);

                return (
                  <>
                    <div className="flex items-center justify-between text-xs text-gray-400 px-1">
                      <span>Arrastra la barra de cada grafica para cambiar el orden en que se muestran</span>
                      {chartOrder.join(',') !== DEFAULT_CHART_ORDER.join(',') && (
                        <button
                          onClick={resetChartOrder}
                          className="flex items-center gap-1 text-gray-500 hover:text-blue-600 transition"
                        >
                          <RotateCcw size={12} />
                          Restablecer orden
                        </button>
                      )}
                    </div>

                    {orderedCharts.map(chart => (
                      <div
                        key={chart.id}
                        onDragOver={handleChartDragOver}
                        onDrop={handleChartDrop(chart.id)}
                        className={`transition-opacity ${draggedChartId === chart.id ? 'opacity-40' : 'opacity-100'}`}
                      >
                        <div
                          draggable
                          onDragStart={handleChartDragStart(chart.id)}
                          onDragEnd={handleChartDragEnd}
                          className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600 text-xs mb-1 px-1 py-1 cursor-grab active:cursor-grabbing select-none w-fit"
                          title="Arrastra para reordenar esta grafica"
                        >
                          <GripVertical size={14} />
                          <span>Mover</span>
                        </div>
                        {chart.node}
                      </div>
                    ))}
                  </>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* TAB 2: VENTAS Y GASTOS */}
      {/* ============================================ */}
      {activeTab === 'ventas' && (
        <div className="space-y-6">
          {/* Filtro de Fecha + Boton Nuevo Pago */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <Calendar className="text-blue-600" size={24} />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar Fecha
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={exportToXLSX}
                  disabled={exportingReport}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:from-emerald-700 hover:to-teal-700 transition font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" />
                  {exportingReport ? 'Generando...' : 'Reporte Excel'}
                </button>
                <button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, date: selectedDate }));
                    setShowPaymentModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition font-semibold"
                >
                  <Plus className="w-4 h-4" />
                  Registrar Ingreso/Egreso
                </button>
              </div>
            </div>
          </div>

          {/* Tarjetas financieras del dia */}
          {(() => {
            const dayTotals = getDayPaymentTotals();
            const dayBalance = stats.total_revenue + dayTotals.income - dayTotals.expenses;
            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-md p-5 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm font-medium">Ventas del Dia</p>
                      <p className="text-2xl font-bold mt-1">{stats.total_sales}</p>
                      <p className="text-sm text-blue-100 mt-1">{formatCurrency(stats.total_revenue)}</p>
                    </div>
                    <ShoppingBag size={36} className="opacity-80" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-md p-5 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-red-100 text-sm font-medium">Egresos</p>
                      <p className="text-2xl font-bold mt-1">{formatCurrency(dayTotals.expenses)}</p>
                    </div>
                    <ArrowDownRight size={36} className="opacity-80" />
                  </div>
                </div>

                <div className={`bg-gradient-to-r ${dayBalance >= 0 ? 'from-teal-500 to-teal-600' : 'from-orange-500 to-orange-600'} rounded-lg shadow-md p-5 text-white`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-medium opacity-80">Balance del Dia</p>
                      <p className="text-2xl font-bold mt-1">{formatCurrency(dayBalance)}</p>
                    </div>
                    <DollarSign size={36} className="opacity-80" />
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Historial de Ventas */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Historial de Ventas</h3>

            {sales.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ShoppingBag size={48} className="mx-auto mb-4 opacity-30" />
                <p>No hay ventas registradas para esta fecha</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sales.map((sale) => (
                  <div
                    key={sale.id}
                    className={`border-2 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition ${sale.cancelled ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
                    onClick={() => {
                      setSelectedSale(sale);
                      setShowSaleModal(true);
                    }}
                  >
                    <div className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <p className="text-sm font-semibold text-gray-700">
                              Pedido <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold">{getDayCode(sale, sales)}</span>
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatEcuadorTime(sale.created_at)}
                            </p>
                            {sale.cancelled && (
                              <div className="flex flex-col gap-1">
                                <span className="px-2 py-1 bg-red-200 text-red-800 text-xs font-bold rounded-full inline-block w-fit">
                                  ANULADA
                                </span>
                                {sale.cancel_reason && (
                                  <span className="text-xs text-red-700 italic">
                                    Motivo: {sale.cancel_reason}
                                  </span>
                                )}
                              </div>
                            )}
                            {/* ⭐ Pedido tomado pero todavía sin cobrar -- se sigue
                                listando acá (el dueño necesita verlo), pero ya NO
                                cuenta en las tarjetas de "Ventas del Día"/balance
                                de arriba (ver stats más abajo, filtrado a 'paid'). */}
                            {!sale.cancelled && sale.payment_status === 'pending' && (
                              <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded-full inline-block w-fit">
                                PENDIENTE DE PAGO
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-2">
                            <p className="text-lg font-bold text-gray-900">
                              Total: <span className={sale.cancelled ? 'text-red-600 line-through' : 'text-green-600'}>
                                ${sale.total.toFixed(2)}
                              </span>
                            </p>
                            <p className="text-sm text-gray-600">
                              {sale.payment_method === 'cash' && 'Efectivo'}
                              {sale.payment_method === 'transfer' && 'Transferencia'}
                              {sale.payment_method === 'mixed' && 'Mixto'}
                            </p>
                            {(sale.table_name || sale.table_id) && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                                Mesa {sale.table_name || sale.table_id}
                              </span>
                            )}
                            {!sale.cancelled && (
                              <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${
                                sale.receipt_printed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                              }`}>
                                <Printer className="w-3 h-3" />
                                {sale.receipt_printed ? 'Recibo impreso' : 'Sin imprimir'}
                              </span>
                            )}
                            {sale.billing_type === 'factura' && (
                              <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold">
                                Factura
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSale(sale);
                            setShowSaleModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 font-semibold text-sm"
                        >
                          Ver detalles
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Seccion de Pagos Manuales del dia */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-600" />
                Ingresos y Egresos
              </h3>
              <button
                onClick={() => setShowPaymentsSection(!showPaymentsSection)}
                className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
              >
                {showPaymentsSection ? 'Ocultar historial completo' : 'Ver historial completo'}
              </button>
            </div>

            {/* Pagos del dia seleccionado */}
            {(() => {
              const dayPayments = payments.filter(p => (p.date || '').split('T')[0] === selectedDate);
              if (dayPayments.length === 0) {
                return (
                  <p className="text-center text-gray-500 py-4 text-sm">No hay ingresos/egresos registrados para esta fecha</p>
                );
              }
              return (
                <div className="space-y-2">
                  {dayPayments.map(payment => (
                    <div key={payment.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(payment.type)}`}>
                          {payment.type === 'income' ? 'Ingreso' : 'Egreso'}
                        </span>
                        <div>
                          <p className="font-medium text-gray-800 text-sm">{payment.description}</p>
                          <p className="text-xs text-gray-500">{getCategoryLabel(payment.type, payment.category)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`font-bold ${payment.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                          {payment.type === 'income' ? '+' : '-'}${payment.amount.toFixed(2)}
                        </span>
                        <div className="flex gap-1">
                          <button onClick={() => handleEditPayment(payment)} className="text-blue-600 hover:text-blue-800 p-1">
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleDeletePayment(payment.id)} className="text-red-600 hover:text-red-800 p-1">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Historial completo de pagos (expandible) */}
            {showPaymentsSection && (
              <div className="mt-6 border-t pt-4">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Historial Completo de Pagos</h4>

                {/* Filtros */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                  <select
                    value={filters.type}
                    onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="all">Todos</option>
                    <option value="income">Ingresos</option>
                    <option value="expense">Egresos</option>
                  </select>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Desde"
                  />
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Hasta"
                  />
                  <button
                    onClick={loadPayments}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    <Filter className="w-3.5 h-3.5" />
                    Filtrar
                  </button>
                </div>

                {/* Totales generales */}
                {(() => {
                  const filtered = payments.filter(p => {
                    if (filters.type !== 'all' && p.type !== filters.type) return false;
                    if (filters.dateFrom && p.date < filters.dateFrom) return false;
                    if (filters.dateTo && p.date > filters.dateTo) return false;
                    return true;
                  });
                  const income = filtered.filter(p => p.type === 'income').reduce((sum, p) => sum + p.amount, 0);
                  const expenses = filtered.filter(p => p.type === 'expense').reduce((sum, p) => sum + p.amount, 0);
                  const bal = income - expenses;
                  return (
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                        <p className="text-xs text-green-600 font-medium">Ingresos</p>
                        <p className="text-lg font-bold text-green-700">${income.toFixed(2)}</p>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                        <p className="text-xs text-red-600 font-medium">Egresos</p>
                        <p className="text-lg font-bold text-red-700">${expenses.toFixed(2)}</p>
                      </div>
                      <div className={`${bal >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'} border rounded-lg p-3 text-center`}>
                        <p className={`text-xs font-medium ${bal >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Balance</p>
                        <p className={`text-lg font-bold ${bal >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>${bal.toFixed(2)}</p>
                      </div>
                    </div>
                  );
                })()}

                {/* Tabla de pagos */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descripcion</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loadingPayments ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-4 text-center text-gray-500">Cargando...</td>
                        </tr>
                      ) : payments.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-4 text-center text-gray-500">No hay pagos registrados</td>
                        </tr>
                      ) : (
                        payments.map((payment) => (
                          <tr key={payment.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {new Date(payment.date).toLocaleDateString('es-ES')}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(payment.type)}`}>
                                {payment.type === 'income' ? 'Ingreso' : 'Egreso'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {getCategoryLabel(payment.type, payment.category)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              <p className="font-medium">{payment.description}</p>
                              {payment.reference && <p className="text-xs text-gray-500">Ref: {payment.reference}</p>}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold">
                              <span className={payment.type === 'income' ? 'text-green-600' : 'text-red-600'}>
                                {payment.type === 'income' ? '+' : '-'}${payment.amount.toFixed(2)}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <div className="flex gap-2">
                                <button onClick={() => handleEditPayment(payment)} className="text-blue-600 hover:text-blue-800">
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDeletePayment(payment.id)} className="text-red-600 hover:text-red-800">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Historial de Cierres de Caja */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-gray-600" />
              Historial de Cierres de Caja
            </h3>
            {loadingCashSessions ? (
              <p className="text-center text-gray-500 py-6">Cargando...</p>
            ) : cashSessions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Lock size={48} className="mx-auto mb-4 opacity-30" />
                <p>Aún no se ha cerrado ninguna caja</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Abierta</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cerrada</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cerrada por</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ventas</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Esperado</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Contado</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Diferencia</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cashSessions.map((cs) => (
                      <tr key={cs.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {formatEcuadorDate(cs.opened_at)} {formatEcuadorTime(cs.opened_at)}
                          {cs.opened_by_name && <span className="block text-xs text-gray-400">{cs.opened_by_name}</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {formatEcuadorDate(cs.closed_at)} {formatEcuadorTime(cs.closed_at)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {cs.closed_by_name || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {cs.total_sales_count} (${Number(cs.total_sales_amount || 0).toFixed(2)})
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">${Number(cs.expected_cash || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">${Number(cs.counted_cash || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-bold">
                          <span className={Math.abs(cs.difference) < 0.01 ? 'text-green-600' : cs.difference < 0 ? 'text-red-600' : 'text-blue-600'}>
                            {cs.difference > 0 ? '+' : ''}${Number(cs.difference || 0).toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Modal de Detalles de Venta */}
          {showSaleModal && selectedSale && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-blue-100">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        Pedido <span className="bg-blue-600 text-white px-3 py-1 rounded-lg">{getDayCode(selectedSale, sales)}</span>
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {formatEcuadorDate(selectedSale.created_at)} - {formatEcuadorTime(selectedSale.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={() => { setShowSaleModal(false); setEditingSaleItems(null); }}
                      className="text-gray-500 hover:text-gray-700 text-2xl font-bold w-8 h-8 flex items-center justify-center"
                    >
                      &times;
                    </button>
                  </div>
                  {selectedSale.cancelled && (
                    <div className="mt-3 bg-red-100 border border-red-300 rounded-lg p-3">
                      <p className="text-sm text-red-800 font-semibold mb-1">VENTA ANULADA</p>
                      {selectedSale.cancel_reason && (
                        <p className="text-xs text-red-700"><strong>Motivo:</strong> {selectedSale.cancel_reason}</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="p-6">
                  {/* Productos */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      Productos
                    </h4>
                    <div className="space-y-2">
                      {selectedSale.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">{item.name}</p>
                            <p className="text-sm text-gray-500">{item.quantity} x ${item.price.toFixed(2)}</p>
                          </div>
                          <p className="font-bold text-gray-900 text-lg">${(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Informacion de Pago */}
                  <div className="mb-6 bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-3">Informacion de Pago</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Metodo de pago:</p>
                        <p className="font-semibold text-gray-900">
                          {selectedSale.payment_method === 'cash' && 'Efectivo'}
                          {selectedSale.payment_method === 'transfer' && 'Transferencia'}
                          {selectedSale.payment_method === 'mixed' && 'Mixto'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total:</p>
                        <p className="text-2xl font-bold text-green-600">${selectedSale.total.toFixed(2)}</p>
                      </div>

                      {selectedSale.payment_method === 'mixed' && (
                        <>
                          <div>
                            <p className="text-sm text-gray-600">Efectivo:</p>
                            <p className="font-semibold text-gray-900">${(selectedSale.cash_received || 0).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Transferencia:</p>
                            <p className="font-semibold text-gray-900">${(selectedSale.transfer_amount || 0).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Cambio:</p>
                            <p className="font-semibold text-green-600">${(selectedSale.change_given || 0).toFixed(2)}</p>
                          </div>
                        </>
                      )}

                      {selectedSale.payment_method === 'cash' && selectedSale.cash_received && (
                        <>
                          <div>
                            <p className="text-sm text-gray-600">Recibido:</p>
                            <p className="font-semibold text-gray-900">${selectedSale.cash_received.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Cambio:</p>
                            <p className="font-semibold text-green-600">${(selectedSale.change_given || 0).toFixed(2)}</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Informacion adicional */}
                  <div className="mb-6 border-t pt-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">Tipo de pedido:</p>
                        <p className="font-semibold text-gray-800">
                          {selectedSale.order_type === 'servir' ? 'Para Servir' : 'Para Llevar'}
                        </p>
                      </div>
                      {selectedSale.customer_name && (
                        <div>
                          <p className="text-gray-500">Cliente:</p>
                          <p className="font-semibold text-gray-800">{selectedSale.customer_name}</p>
                        </div>
                      )}
                      {(selectedSale.table_name || selectedSale.table_id) && (
                        <div>
                          <p className="text-gray-500">Mesa:</p>
                          <p className="font-semibold text-gray-800">Mesa {selectedSale.table_name || selectedSale.table_id}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-gray-500">Recibo:</p>
                        <p className={`font-semibold ${selectedSale.receipt_printed ? 'text-green-600' : 'text-gray-500'}`}>
                          {selectedSale.receipt_printed ? 'Impreso' : 'No se imprimió'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Facturación (feature #11): billing_data es un snapshot JSON
                      guardado al momento de la venta, ver POST /api/pos/sales */}
                  {selectedSale.billing_type === 'factura' && (() => {
                    let billingCustomer = null;
                    try {
                      billingCustomer = selectedSale.billing_data ? JSON.parse(selectedSale.billing_data) : null;
                    } catch (_) { /* dato corrupto o antiguo, se ignora */ }
                    return (
                      <div className="mb-6 bg-indigo-50 rounded-lg p-4">
                        <h4 className="font-semibold text-indigo-800 mb-3">Datos de Facturación</h4>
                        {billingCustomer ? (
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-gray-500">Nombre:</p>
                              <p className="font-semibold text-gray-900">{billingCustomer.first_name} {billingCustomer.last_name}</p>
                            </div>
                            {billingCustomer.cedula && (
                              <div><p className="text-gray-500">Cédula:</p><p className="font-semibold text-gray-900">{billingCustomer.cedula}</p></div>
                            )}
                            {billingCustomer.phone && (
                              <div><p className="text-gray-500">Teléfono:</p><p className="font-semibold text-gray-900">{billingCustomer.phone}</p></div>
                            )}
                            {billingCustomer.email && (
                              <div><p className="text-gray-500">Correo:</p><p className="font-semibold text-gray-900 break-all">{billingCustomer.email}</p></div>
                            )}
                            {billingCustomer.sector && (
                              <div><p className="text-gray-500">Sector:</p><p className="font-semibold text-gray-900">{billingCustomer.sector}</p></div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">Esta venta se facturó, pero no quedaron datos de cliente guardados.</p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Tiempo de cocina: mismo calculo real-vs-estimado que la tabla
                      "Tiempo Real vs Estimado por Pedido" del dashboard, pero para
                      este pedido puntual. */}
                  {selectedSale.kitchen_started_at && (() => {
                    const start = new Date(selectedSale.kitchen_started_at);
                    const end = selectedSale.kitchen_done_at ? new Date(selectedSale.kitchen_done_at) : null;
                    const realMinutes = end ? (end - start) / 60000 : null;
                    const estimado = selectedSale.kitchen_estimated_minutes;
                    const valid = realMinutes != null && realMinutes > 0 && realMinutes < 180;
                    return (
                      <div className="mb-6 bg-amber-50 rounded-lg p-4">
                        <h4 className="font-semibold text-amber-800 mb-3">Tiempo de Cocina</h4>
                        {!end ? (
                          <p className="text-sm text-gray-600">Este pedido todavía no fue marcado como listo en Cocina.</p>
                        ) : !valid ? (
                          <p className="text-sm text-gray-600">Tiempo no disponible (dato fuera de rango).</p>
                        ) : (
                          <div className="grid grid-cols-3 gap-3 text-sm">
                            <div>
                              <p className="text-gray-500">Real:</p>
                              <p className="font-semibold text-gray-900">{realMinutes.toFixed(1)} min</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Estimado:</p>
                              <p className="font-semibold text-gray-900">{estimado != null ? `${Number(estimado).toFixed(1)} min` : '—'}</p>
                            </div>
                            {estimado != null && (
                              <div>
                                <p className="text-gray-500">Diferencia:</p>
                                <p className={`font-semibold ${realMinutes - estimado > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                  {realMinutes - estimado > 0 ? '+' : ''}{(realMinutes - estimado).toFixed(1)} min
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ⭐ Edición de pedido ya pagado -- ver openEditSale/handleSaveSaleEdit.
                      Reemplaza los botones normales mientras está activa. */}
                  {editingSaleItems ? (
                    <div className="mb-6 border-2 border-blue-200 rounded-lg p-4 bg-blue-50 space-y-4">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                        Editar un pedido pagado NO ajusta automáticamente el efectivo ya cobrado ni el cierre de caja de ese turno.
                        Úsalo solo para corregir errores de registro. El cambio queda guardado en Auditoría con el motivo que escribas.
                      </div>
                      <div className="space-y-2">
                        {editingSaleItems.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between gap-2 bg-white rounded-lg p-2.5 border border-gray-200">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                              <p className="text-xs text-gray-500">${Number(item.price).toFixed(2)} c/u</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button onClick={() => updateEditSaleItemQty(idx, -1)} className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-50 border border-gray-300 text-gray-600 hover:bg-gray-100">-</button>
                              <span className="w-6 text-center text-sm font-bold">{item.quantity}</span>
                              <button onClick={() => updateEditSaleItemQty(idx, 1)} className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-50 border border-gray-300 text-gray-600 hover:bg-gray-100">+</button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between items-center border-t pt-3">
                        <span className="font-semibold text-gray-700">Nuevo total:</span>
                        <span className="text-xl font-bold text-green-600">${editingSaleTotal.toFixed(2)}</span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Motivo de la edición <span className="text-red-500">*</span></label>
                        <textarea
                          value={editSaleReason}
                          onChange={(e) => setEditSaleReason(e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          placeholder="Ej: se registró una cantidad equivocada por error"
                        />
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setEditingSaleItems(null)}
                          disabled={savingSaleEdit}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Cancelar edición
                        </button>
                        <button
                          onClick={handleSaveSaleEdit}
                          disabled={savingSaleEdit}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50"
                        >
                          {savingSaleEdit ? 'Guardando...' : 'Guardar cambios'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Botones */}
                      <div className="flex gap-3">
                        {!selectedSale.cancelled && (
                          <button
                            onClick={() => {
                              handleCancelSale(selectedSale.id);
                              setShowSaleModal(false);
                            }}
                            className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition font-bold"
                          >
                            Anular Venta
                          </button>
                        )}
                        {!selectedSale.cancelled && selectedSale.payment_status === 'paid' && canEditPaidOrders && (
                          <button
                            onClick={openEditSale}
                            className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-bold"
                          >
                            Editar Pedido
                          </button>
                        )}
                        <button
                          onClick={() => setShowSaleModal(false)}
                          className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition font-bold"
                        >
                          Cerrar
                        </button>
                      </div>
                    </>
                  )}

                  {selectedSale.cancelled && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-700">Esta venta fue anulada. El stock fue restaurado automaticamente.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de formulario de Pagos */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">
                  {editingPayment ? 'Editar Pago' : 'Nuevo Ingreso/Egreso'}
                </h3>
                <button onClick={handleClosePaymentModal} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">&times;</button>
              </div>

              <form onSubmit={handleSubmitPayment} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value, category: e.target.value === 'income' ? 'sale' : 'purchase' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      required
                    >
                      <option value="expense">Egreso</option>
                      <option value="income">Ingreso</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Categoria <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      required
                    >
                      {categories[formData.type].map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descripcion <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Ej: Pago a proveedor XYZ"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monto <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Metodo de Pago
                    </label>
                    <select
                      value={formData.payment_method}
                      onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="cash">Efectivo</option>
                      <option value="transfer">Transferencia</option>
                      <option value="card">Tarjeta</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Referencia/No. de Factura
                  </label>
                  <input
                    type="text"
                    value={formData.reference}
                    onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Ej: FAC-001234"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas adicionales
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows="3"
                    placeholder="Notas opcionales..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleClosePaymentModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loadingPayments}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-semibold"
                  >
                    {loadingPayments ? 'Guardando...' : editingPayment ? 'Actualizar' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportesUnificado;
